import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ChevronRight } from 'lucide-react';
import { get } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { RateText } from '@/components/atoms/RateText';
import { PriceText } from '@/components/atoms/PriceText';
import { comma, isoDate } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { AdBanner } from '@/components/ads/AdBanner';

type DiaryPeriod = 'all' | 'today' | 'week' | 'month' | 'year';

const PERIOD_TABS: { id: DiaryPeriod; label: string }[] = [
  { id: 'all', label: '전체' },
  { id: 'today', label: '오늘' },
  { id: 'week', label: '최근 7일' },
  { id: 'month', label: '이번 달' },
  { id: 'year', label: '올해' },
];

const PERIOD_LABEL: Record<DiaryPeriod, string> = {
  all: '전체 기간',
  today: '오늘',
  week: '최근 7일',
  month: '이번 달',
  year: '올해',
};

/* 정렬 옵션 — 백엔드 DiarySortKey 와 동기화 */
type DiarySortKey =
  | 'latest'
  | 'oldest'
  | 'holding'
  | 'closed'
  | 'profit_desc'
  | 'profit_asc';

const SORT_OPTIONS: { id: DiarySortKey; label: string }[] = [
  { id: 'latest',      label: '최신순' },
  { id: 'oldest',      label: '오래된순' },
  { id: 'holding',     label: '보유중 먼저' },
  { id: 'closed',      label: '청산 먼저' },
  { id: 'profit_desc', label: '수익률 높은순' },
  { id: 'profit_asc',  label: '수익률 낮은순' },
];

interface Position {
  ticker: string;
  asset_type: 'stock' | 'coin' | 'us_stock';
  ticker_name: string | null;
  market: string | null;
  total_buy_qty: number;
  total_buy_amount: number;
  avg_buy_price: number | null;
  total_sell_qty: number;
  total_sell_amount: number;
  avg_sell_price: number | null;
  holding_qty: number;
  realized_pnl: number | null;
  realized_pnl_rate: number | null;
  tx_count: number;
  buy_tx_count: number;
  sell_tx_count: number;
  first_tx_date: string | null;
  last_tx_date: string | null;
  first_buy_date: string | null;
  last_sell_date: string | null;
  last_created_at: string | null;
  status: 'holding' | 'closed' | 'sell_only';
}

export function DiaryListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const setLoginModalOpen = useUIStore((s) => s.setLoginModalOpen);
  const goCreate = () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    navigate('/diary/new');
  };

  /* URL 쿼리 파라미터에서 period / sort 읽기 (기본값 'all' / 'latest') */
  const rawPeriod = searchParams.get('period');
  const period: DiaryPeriod = (
    ['all', 'today', 'week', 'month', 'year'] as const
  ).includes(rawPeriod as DiaryPeriod)
    ? (rawPeriod as DiaryPeriod)
    : 'all';

  const rawSort = searchParams.get('sort');
  const sort: DiarySortKey = SORT_OPTIONS.some((o) => o.id === rawSort)
    ? (rawSort as DiarySortKey)
    : 'latest';

  /** URL 을 깔끔하게 유지 — 기본값(all/latest)은 쿼리 파라미터에 남기지 않음 */
  const updateSearch = (nextPeriod: DiaryPeriod, nextSort: DiarySortKey) => {
    const params: Record<string, string> = {};
    if (nextPeriod !== 'all') params.period = nextPeriod;
    if (nextSort !== 'latest') params.sort = nextSort;
    setSearchParams(params, { replace: true });
  };

  const handlePeriodChange = (next: DiaryPeriod) => updateSearch(next, sort);
  const handleSortChange = (next: DiarySortKey) => updateSearch(period, next);

  const { data, isLoading } = useQuery({
    queryKey: ['diaries', 'positions', period, sort],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (period !== 'all') qs.set('period', period);
      if (sort !== 'latest') qs.set('sort', sort);
      const query = qs.toString();
      return get<{ positions: Position[] }>(
        query ? `/diaries/positions?${query}` : '/diaries/positions',
      );
    },
  });

  const positions = data?.positions ?? [];

  /* ── 포트폴리오 요약 통계 ───────────────────── */
  const summary = {
    totalTickers: positions.length,
    holding: positions.filter((p) => p.status === 'holding').length,
    closed: positions.filter((p) => p.status === 'closed').length,
    sellOnly: positions.filter((p) => p.status === 'sell_only').length,
    totalRealizedPnl: positions.reduce(
      (sum, p) => sum + (p.realized_pnl ?? 0),
      0,
    ),
    realizedPositionsCount: positions.filter((p) => p.realized_pnl !== null).length,
    avgRealizedRate:
      (() => {
        const withRate = positions.filter((p) => p.realized_pnl_rate !== null);
        if (withRate.length === 0) return null;
        return (
          withRate.reduce((s, p) => s + (p.realized_pnl_rate ?? 0), 0) / withRate.length
        );
      })(),
    totalBuyAmount: positions.reduce((s, p) => s + p.total_buy_amount, 0),
    totalSellAmount: positions.reduce((s, p) => s + p.total_sell_amount, 0),
  };

  return (
    <div className="container-base py-10">
      {/* 게시판 링크 영역 — 본인 매매일지 페이지에서 공개 게시판으로 진입 */}
      <div className="mb-4">
        <Link
          to="/board/diary"
          className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-text-sub)] transition-colors hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/5 hover:text-[var(--color-primary)]"
        >
          매매일지 게시판
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold">매매일지</h1>
          <p className="mt-2 text-sm text-[var(--color-text-sub)]">
            내 매매 기록을 남기고 수익률·보유 현황을 한 눈에 확인해 보세요.
          </p>
        </div>
        <Button onClick={goCreate}>
          <Plus className="h-4 w-4" />
          종목 추가
        </Button>
      </header>

      {/* 필터 영역 — 기간 칩 + 정렬 드롭다운 한 줄 배치 (모바일에선 2줄) */}
      <div className="mb-5 flex flex-col gap-2 rounded-2xl border border-[var(--color-border)] bg-white p-3 md:flex-row md:items-center md:justify-between">
        {/* 기간 필터 탭 */}
        <div className="flex flex-wrap gap-1.5 overflow-x-auto">
          {PERIOD_TABS.map((tab) => {
            const active = tab.id === period;
            return (
              <button
                key={tab.id}
                onClick={() => handlePeriodChange(tab.id)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap',
                  active
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-sub)] hover:border-[var(--color-primary)]/40 hover:text-[var(--color-text)]',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* 정렬 드롭다운 — 옵션이 6개라 칩 대신 select 로 간결하게 */}
        <label className="flex shrink-0 items-center gap-2 text-xs text-[var(--color-text-sub)]">
          정렬
          <select
            value={sort}
            onChange={(e) => handleSortChange(e.target.value as DiarySortKey)}
            className={cn(
              'h-8 rounded-full border px-3 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30',
              sort === 'latest'
                ? 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
                : 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)]',
            )}
            aria-label="정렬 기준"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* 포트폴리오 요약 */}
      {!isLoading && positions.length > 0 && (
        <>
          <section className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatBox
              label={period === 'all' ? '보유 종목' : `${PERIOD_LABEL[period]} 거래`}
              value={`${summary.totalTickers}종`}
              sub={
                summary.totalTickers > 0
                  ? `보유 ${summary.holding} · 청산 ${summary.closed}${summary.sellOnly > 0 ? ` · 매도선행 ${summary.sellOnly}` : ''}`
                  : undefined
              }
            />
            <StatBox
              label="총 매수금액"
              value={<PriceText value={summary.totalBuyAmount} />}
            />
            <StatBox
              label="총 실현손익"
              value={
                summary.realizedPositionsCount > 0 ? (
                  <PriceText
                    value={summary.totalRealizedPnl}
                    trend={summary.totalRealizedPnl >= 0 ? 'up' : 'down'}
                  />
                ) : (
                  <span className="text-[var(--color-text-sub)]">—</span>
                )
              }
              sub={
                summary.realizedPositionsCount > 0
                  ? `${summary.realizedPositionsCount}종 실현`
                  : '실현 없음'
              }
            />
            <StatBox
              label="평균 수익률"
              value={
                summary.avgRealizedRate !== null ? (
                  <RateText value={summary.avgRealizedRate} size="lg" />
                ) : (
                  <span className="text-[var(--color-text-sub)]">—</span>
                )
              }
              sub={
                summary.avgRealizedRate !== null
                  ? `실현 종목 기준`
                  : '실현 없음'
              }
            />
          </section>
        </>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}

      {/* 포지션 카드 리스트 */}
      {!isLoading && (
        <div className="space-y-3">
          {positions.map((p) => (
            <PositionCard
              key={p.ticker}
              position={p}
              onClick={() =>
                navigate(`/diary/ticker/${encodeURIComponent(p.ticker)}`)
              }
            />
          ))}
          {positions.length === 0 && (
            <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-[var(--color-text-sub)]">
              {period === 'all' ? (
                <>
                  <p className="mb-4">아직 등록된 종목이 없어요.</p>
                  <Button onClick={goCreate}>
                    <Plus className="h-4 w-4" />첫 종목 추가하기
                  </Button>
                </>
              ) : (
                <>
                  <p className="mb-2">
                    <strong>{PERIOD_LABEL[period]}</strong> 에 거래 내역이 없어요.
                  </p>
                  <button
                    onClick={() => handlePeriodChange('all')}
                    className="text-xs text-[var(--color-primary)] hover:underline"
                  >
                    전체 기간으로 보기
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* 페이지 하단 광고 — 정형 디스플레이 (모바일 가변 높이 회피) */}
      <AdBanner slot="display-rect" className="mt-4 sm:mt-8" />
    </div>
  );
}

function PositionCard({
  position: p,
  onClick,
}: {
  position: Position;
  onClick: () => void;
}) {
  /* 상태 배지 — 가로 크기 고정 (min-w-[52px]) */
  const badge =
    p.status === 'holding'
      ? {
          label: '보유 중',
          cls: 'bg-[var(--color-fall)]/10 text-[var(--color-fall)] border-[var(--color-fall)]/20',
        }
      : p.status === 'closed'
      ? {
          label: '청산',
          cls: 'bg-[var(--color-rise)]/10 text-[var(--color-rise)] border-[var(--color-rise)]/20',
        }
      : {
          label: '매도 선행',
          cls: 'bg-[var(--color-text-sub)]/10 text-[var(--color-text-sub)] border-[var(--color-text-sub)]/20',
        };

  const displayName = p.ticker_name ?? p.ticker;
  const showCodeChip = p.ticker_name && p.ticker_name !== p.ticker;

  return (
    <button
      onClick={onClick}
      className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left transition-colors hover:border-[var(--color-primary)]/40"
    >
      <div className="flex items-start justify-between gap-3">
        {/* 왼쪽: 종목 정보 */}
        <div className="min-w-0 flex-1">
          {/* 상태 배지 (가로 크기 고정) + 종목명 */}
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex min-w-[52px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge.cls}`}
            >
              {badge.label}
            </span>
            <span className="truncate font-bold">{displayName}</span>
            {showCodeChip && (
              <span className="rounded bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-sub)]">
                {p.ticker}
              </span>
            )}
          </div>

          {/* 수량 상세: 총매수 / 총매도 / 보유 */}
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-[var(--color-text-sub)] md:grid-cols-3">
            <span>
              총매수 <strong className="text-[var(--color-text)]">{comma(p.total_buy_qty)}</strong>
            </span>
            <span>
              총매도 <strong className="text-[var(--color-text)]">{comma(p.total_sell_qty)}</strong>
            </span>
            <span>
              보유{' '}
              <strong
                className={
                  p.holding_qty > 0
                    ? 'text-[var(--color-primary)]'
                    : 'text-[var(--color-text)]'
                }
              >
                {comma(p.holding_qty)}
              </strong>
            </span>
          </div>

          {/* 날짜: 최초 매수일 + 최근 매도일 */}
          <div className="mt-1 space-y-0.5 text-[11px] text-[var(--color-text-sub)]">
            {p.first_buy_date && (
              <div>
                최초 매수일 : <span className="text-[var(--color-text)]">{isoDate(p.first_buy_date)}</span>
              </div>
            )}
            {p.last_sell_date && (
              <div>
                최근 매도일 : <span className="text-[var(--color-text)]">{isoDate(p.last_sell_date)}</span>
              </div>
            )}
          </div>

          {/* 거래 건수: 매수 N건, 매도 N건 */}
          <div className="mt-1 text-[10px] text-[var(--color-text-sub)]">
            매수 {p.buy_tx_count}건, 매도 {p.sell_tx_count}건
          </div>
        </div>

        {/* 오른쪽: 수익률 + 실현손익 + 화살표 */}
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-end gap-1.5">
            {p.realized_pnl !== null && p.realized_pnl_rate !== null ? (
              <>
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-[var(--color-text-sub)]">수익률</span>
                  <strong
                    className={
                      p.realized_pnl_rate >= 0
                        ? 'text-[var(--color-rise)]'
                        : 'text-[var(--color-fall)]'
                    }
                  >
                    {p.realized_pnl_rate >= 0 ? '+' : ''}
                    {p.realized_pnl_rate.toFixed(2)}%
                  </strong>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-[var(--color-text-sub)]">실현손익 :</span>
                  <strong
                    className={
                      p.realized_pnl >= 0
                        ? 'text-[var(--color-rise)]'
                        : 'text-[var(--color-fall)]'
                    }
                  >
                    {p.realized_pnl >= 0 ? '+' : ''}
                    {comma(Math.round(p.realized_pnl))}원
                  </strong>
                </div>
              </>
            ) : (
              <span className="text-xs text-[var(--color-text-sub)]">손익 미확정</span>
            )}
          </div>
          <ChevronRight className="h-5 w-5 flex-shrink-0 text-[var(--color-text-sub)]" />
        </div>
      </div>
    </button>
  );
}

function StatBox({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="text-xs text-[var(--color-text-sub)]">{label}</div>
      <div className="mt-1 text-lg font-bold">{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-[var(--color-text-sub)]">{sub}</div>}
    </div>
  );
}
